import {
    useState,
    MouseEvent,
    useCallback,
    ChangeEvent,
    useEffect,
} from 'react';
import { flip, offset, shift, size, useFloating } from '@floating-ui/react-dom';
import { PlusIcon } from 'lucide-react';

import { UserResult } from '@components/search-user/user-result';
import { useDebounce } from '@hooks/use-debounce';
import { useRemoteConfig } from '@hooks/remote-config';

import styles from './search-user.module.scss';
import { SelectedUser } from './selected-user';

export type SearchUserInfo = {
    id: number;
    name: string;
    email: string;
};

type Props = {
    users: SearchUserInfo[];
    onSearch: (query: string) => void;
    onSelect: (id: number) => void;
    onRemove: () => void;
};

const SearchUser = ({ users, onSearch, onSelect, onRemove }: Props) => {
    const { getRemoteValue } = useRemoteConfig();

    const [isFocused, setIsFocused] = useState(false);
    const [user, setUser] = useState<SearchUserInfo | undefined>(undefined);
    const [query, setQuery] = useState('');
    const searchQuery = useDebounce(query, 500);

    const { refs, floatingStyles } = useFloating({
        middleware: [
            flip(),
            offset(4),
            shift(),
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                    });
                },
            }),
        ],
    });

    useEffect(() => {
        onSearch(searchQuery);
    }, [searchQuery]);

    const handleUserSelection = (ev: MouseEvent) => {
        ev.preventDefault();
        const option = (ev.target as HTMLElement).closest('[role=user-option]');
        if (!option) return;
        const userId = Number((option as HTMLElement).dataset.id);
        onSelect(userId);
        setUser(users.find((user) => user.id === userId));
        (refs.reference.current as any).blur();
    };

    const clearUser = useCallback(() => {
        setUser(undefined);
        onRemove();
    }, [setUser, onRemove]);

    const handleQueryChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            setQuery(ev.target.value);
        },
        [searchQuery]
    );

    return (
        <>
            {user ? (
                <SelectedUser user={user} onClear={clearUser} />
            ) : (
                <input
                    ref={refs.setReference}
                    className={styles.input}
                    type="text"
                    placeholder="Buscar usuario"
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onChange={handleQueryChange}
                />
            )}
            {isFocused && (
                <ul
                    className={styles.list}
                    style={floatingStyles}
                    ref={refs.setFloating}
                    onMouseDown={handleUserSelection}
                >
                    {getRemoteValue('backoffice.showCreateUserWhenSearch') && (
                        <li className={styles.createUserOption}>
                            <PlusIcon size={14} strokeWidth={2.5} />
                            <span>Añadir nuevo cliente</span>
                        </li>
                    )}
                    <label>resultados</label>
                    {users.map((user) => (
                        <UserResult key={user.id} user={user} />
                    ))}
                </ul>
            )}
        </>
    );
};

export { SearchUser };
